import { EVENTS, subscribe } from 'ia-util-pubsub'
import { queryShadow, queryShadowAll } from 'ia-util-helpers'

// Authoritative read of the Recharge widget selection at submit time. Independent
// of the price-display events fired by ia-recharge-adapter, so the cart is always
// correct even if an event was missed (e.g. listener lost during a re-render).
// Returns the selected selling_plan id, or null for a one-time purchase.
function readRechargeSellingPlan (root) {
  const radios = queryShadowAll(root, 'input[name="purchaseOption"]')
  const checked = radios.find((r) => r.checked)
  const purchaseOption = checked ? checked.value : 'subscription'
  if (purchaseOption === 'onetime') return null
  const select = queryShadow(root, 'select[name="rc_plan"]')
  return select && select.value ? select.value : null
}

if (!customElements.get('ia-product-buy')) {
  class IaProductBuy extends HTMLElement {
    constructor () {
      super()
      this.form = this.querySelector('form')
      this.submitButton = this.querySelector('button[type="submit"]')
      this.submitButtonTextSpan = this.submitButton?.querySelector('span:not(.button__divider):not(.button__price-compare):not(.button__price)')
      this.textAddToCart = this.submitButton?.dataset.addToCartText || 'Add to Cart'
      this.textSoldOut = this.submitButton?.dataset.soldOutText || 'Sold Out'
      this.textUnavailable = this.submitButton?.dataset.unavailableText || 'Unavailable'
      this.cart = document.querySelector('ia-cart')
      this.variantInput = this.querySelector('input[name="id"]')
      this.quantityInput = this.querySelector('input[name="quantity"]')
      this.planInput = this.querySelector('input[name="selling_plan"]')
      this.gwpVariantId = this.getAttribute('gwp-variant-id') || null
      const bundleData = this.parseBundleData(this.querySelector('[data-bundle-components]'))
      this.bundleComponents = bundleData.components
      this.bundlePlans = bundleData.bundlePlans
      this.bundleTitle = this.getAttribute('data-bundle-title') || null
      this.parentBlock = this.parentElement.closest('ia-main-product') || this.parentElement.closest('ia-product-card')
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmit.bind(this))
      }
      
      this.unsubscribeVariantChange = subscribe(EVENTS.variantChange, this.handleVariantChange.bind(this))
      this.unsubscribeVariantLoading = subscribe(EVENTS.variantLoading, this.handleVariantLoading.bind(this))
      this.unsubscribePlanChange = subscribe(EVENTS.planChange, this.subscriptionChange.bind(this))
      this.unsubscribeCadenceChange = subscribe(EVENTS.cadenceChange, this.subscriptionChange.bind(this))
    
    }

    disconnectedCallback() {
      if (this.unsubscribeVariantChange) {
        this.unsubscribeVariantChange()
      }
      if (this.unsubscribeVariantLoading) {
        this.unsubscribeVariantLoading()
      }
      if (this.unsubscribePlanChange) {
        this.unsubscribePlanChange()
      }
    }

    get sectionId() {
      return this.getAttribute('section-id')
    }

    get productId() {
      return this.getAttribute('product-id')
    }

    handleVariantLoading(event) {
      const { productId, sectionId, loading } = event.detail
      if (productId == this.productId && sectionId == this.sectionId && this.submitButton) {
        if (loading) {
          this.submitButton.classList.add('loading')
        } else {
          this.submitButton.classList.remove('loading')
        }
      }
    }

    handleVariantChange(event) {
      const { productId, sectionId, variant } = event.detail
      if (this.variantInput && productId == this.productId && sectionId == this.sectionId) {
        if (variant) {
          this.variantInput.value = variant.id
          if (this.submitButtonTextSpan) this.submitButtonTextSpan.textContent = variant.available ? this.textAddToCart : this.textSoldOut
        } else {
          this.variantInput.value = ''
          if (this.submitButtonTextSpan) this.submitButtonTextSpan.textContent = this.textUnavailable
        }
      }
    }


    async onSubmit (event) {
      event.preventDefault()

      if (this.submitButton.getAttribute('aria-disabled') === 'true') return

      this.handleErrorMessage()
      this.submitButton.classList.add('adding')

      try {
        const ts = window.iaCartAPI?.getTimestamp()
        this.form.querySelectorAll('[data-property-ts]').forEach((input) => {
          input.value = ts
        })

        const formData = new FormData(this.form)

        // Resolve the selected selling plan (null = one-time). Recharge is the
        // source of truth when present; otherwise read the custom widget dropdown.
        let planId = null
        const useRecharge = this.parentBlock?.dataset?.useRecharge === 'true'
        if (useRecharge) {
          planId = readRechargeSellingPlan(this.parentBlock)
          if (planId) {
            formData.set('selling_plan', planId)
          } else {
            formData.delete('selling_plan')
          }
        } else if (this.parentBlock) {
          planId = this.parentBlock.querySelector('[cadence="subscription"]')?.querySelector('[data-selling-plan-select]')?.value || null
          if (this.planInput && planId) {
            formData.append('selling_plan', planId)
          }
        }

        const gwpId = this.gwpVariantId
        const gwpItem = (gwpId && !this.isVariantInCart(gwpId)) ? { id: gwpId, quantity: 1 } : null

        // BUNDLE: a bundle product should never be added as itself. On every
        // surface (PDP, sticky bar, collection cards, upsells) add its individual
        // components instead, plus the gift. Where a Recharge period selector is
        // reachable (PDP / sticky, both inside ia-main-product) the chosen cadence
        // is applied; elsewhere (cards) there is no selector, so planId is null and
        // the components are added one-time.
        const isBundle = this.bundleComponents.length > 0
        if (isBundle) {
          // Unique id tying this bundle's components to its gift, so the cart can
          // revoke the gift if any component is later removed.
          const bundleId = String(ts)
          const items = this.buildBundleItems(formData, planId, bundleId)
          if (gwpItem) {
            // The gift only survives while ALL components are present: tag it with
            // the bundle id and how many components it requires.
            gwpItem.properties = { _bundle_id: bundleId, _gwp_bundle: '1', _bundle_count: String(items.length) }
            items.push(gwpItem)
          }
          await window.iaCartAPI?.addItemsToCart(items)
        } else if (gwpItem) {
          // Add the main product and its gift-with-purchase in one atomic request.
          try {
            await window.iaCartAPI?.addItemsToCart(this.buildItemsWithGwp(formData, gwpId))
          } catch (gwpError) {
            // Gift unavailable at submit time — never block the main purchase.
            await window.iaCartAPI?.addToCart(formData)
          }
        } else {
          await window.iaCartAPI?.addToCart(formData)
        }
        this.handleSuccess()
      } catch (error) {
        this.handleError(error)
      } finally {
        this.submitButton.classList.remove('adding')
        this.submitButton.removeAttribute('aria-disabled')
      }
    }

    isVariantInCart (variantId) {
      const items = window.iaCartAPI?.cart?.items || []
      return items.some((item) => String(item.variant_id) === String(variantId))
    }

    // Bundle data emitted by ia-product-buy.liquid: `bundlePlans` maps the bundle's
    // own plan ids to a frequency key (the plan name), and each `component` carries
    // its own per-frequency plan ids. Returns empty maps for non-bundles.
    parseBundleData (el) {
      const empty = { components: [], bundlePlans: {} }
      if (!el) return empty
      try {
        const parsed = JSON.parse(el.textContent)
        return {
          components: Array.isArray(parsed.components) ? parsed.components.filter((c) => c && c.id) : [],
          bundlePlans: (parsed.bundlePlans && typeof parsed.bundlePlans === 'object') ? parsed.bundlePlans : {}
        }
      } catch (e) {
        return empty
      }
    }

    // Expand a bundle into its component line items. The Recharge widget returns
    // the bundle's OWN plan id; components live in different selling-plan groups,
    // so we translate the bundle plan -> chosen frequency -> each component's own
    // plan id for that frequency. One-time (planId null) adds components with no
    // plan. Components are NOT linked for deletion (no shared _ts) so each can be
    // removed independently; _bundle_id only tags the instance so the cart can
    // revoke the gift when a component is removed (see removeIncompleteBundleGifts).
    buildBundleItems (formData, planId, bundleId) {
      const properties = {}
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/)
        if (match) properties[match[1]] = value
      }
      if (this.bundleTitle) properties._bundle = this.bundleTitle
      if (bundleId) properties._bundle_id = bundleId

      // Multiply each component's base quantity by the bundle quantity chosen on the PDP.
      const bundleQty = parseInt(formData.get('quantity') || '1', 10) || 1
      // Bundle plan id -> frequency key; null for a one-time purchase.
      const frequency = planId ? this.bundlePlans[planId] : null

      return this.bundleComponents.map((component) => {
        const item = {
          id: component.id,
          quantity: (parseInt(component.quantity, 10) || 1) * bundleQty,
          properties: { ...properties }
        }
        if (frequency) {
          const componentPlan = component.plans && component.plans[frequency]
          if (componentPlan) {
            item.selling_plan = componentPlan
          } else {
            // Component isn't enrolled at this frequency — add it one-time so the
            // whole add still succeeds, and surface it for follow-up.
            console.warn(`[bundle] no subscription plan for variant ${component.id} at "${frequency}" — adding one-time`)
          }
        }
        return item
      })
    }

    // Convert the product form into a cart/add.js items array (preserving the
    // selling plan and line item properties) and append the gift at quantity 1.
    buildItemsWithGwp (formData, gwpId) {
      const main = {
        id: formData.get('id'),
        quantity: parseInt(formData.get('quantity') || '1', 10) || 1,
      }
      const sellingPlan = formData.get('selling_plan')
      if (sellingPlan) main.selling_plan = sellingPlan

      const properties = {}
      for (const [key, value] of formData.entries()) {
        const match = key.match(/^properties\[(.+)\]$/)
        if (match) properties[match[1]] = value
      }
      if (Object.keys(properties).length) main.properties = properties

      return [main, { id: gwpId, quantity: 1 }]
    }

    handleSuccess (data) {
      this.cart?.open(data)
    }

    handleError (error) {
      this.handleErrorMessage(error?.description || error.message || 'An error occurred')
    }

    handleErrorMessage (message = false) {
      const errorElement = this.querySelector('[data-error-message]')
      if (!errorElement) return

      errorElement.classList.toggle('hidden', !message)
      if (message) {
        errorElement.textContent = message
      }
    }

    subscriptionChange(event) {
      const { productId, sectionId, cadence, plan } = event.detail
      if (productId == this.productId && sectionId == this.sectionId) {
        // console.log('[PRODUCT FORM] subscriptionChange, productId:', productId, 'sectionId:', sectionId, 'event:', event);
        this.planInput.value = ''
        if (cadence == 'subscription' && plan && plan.id) {
          // console.log('this.planInput', this.planInput);
          this.planInput.value = plan.id
          
        }
      }
    }    
  }

  customElements.define('ia-product-buy', IaProductBuy)
}
